import { yupResolver } from '@hookform/resolvers/yup'
import { LinePlus } from '@qr-front/icons/line-plus'
import { LineRestaurant } from '@qr-front/icons/line-restaurant'
import { Button } from '@qr-front/ui/components/Button'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import {
	SortDialog,
	SortDialogListItem,
} from 'features/generator/components/sortDialog'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { activeSectionIdAtom } from 'features/generator/qrTypes/Menu/MenuScreen/MenuScreen'
import { useSyncFieldWithAtom } from 'hooks/useSyncFieldWithAtom'
import { useAtom } from 'jotai'
import { atomWithReset, RESET, splitAtom } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { useBus } from 'react-bus'
import { FormProvider, useForm } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { TQrColorScheme } from '../qrColorSchemes'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { getMenuFormSchema, TMenuFormFields } from './menuFormModel'
import { validateMenuForm } from './menuFormUtils'
import { Section } from './Section'
import { emptyProduct, TMenuSectionHandle } from './Section/Section'

/**
 * Adds default visible values for backward compatibility with old QR codes
 */
export const addDefaultVisibleValuesToMenuContent = (
	content: TMenuContent,
): TMenuContent => ({
	...content,
	sections: content.sections.map((section) => ({
		...section,
		visible: section.visible ?? true,
		products: section.products.map((product) => ({
			...product,
			visible: product.visible ?? true,
		})),
	})),
})

export type TMenuSectionProduct = {
	id: number
	image: TUploadedMedia
	localImageUrl?: string | null
	name: string
	nameTranslation: string
	description: string
	descriptionTranslation: string
	price: string
	allergens: string[]
	visible: boolean
}

export type TMenuSection = {
	id: number
	name: string
	nameTranslation: string
	description: string
	descriptionTranslation: string
	products: TMenuSectionProduct[]
	visible: boolean
}

export type TMenuContent = {
	type: QR_TYPE.Menu
	colors: Omit<TQrColorScheme, 'name'>
	info: { name: string; description: string; image: TUploadedMedia }
	sections: TMenuSection[]
	loaderImage: TUploadedMedia
}

export const MENU_FORM_ID = 'menu'
export const CLOSE_MENU_SECTION_ACCORDIONS_EVENT =
	'CLOSE_MENU_SECTION_ACCORDIONS_EVENT'
export const OPEN_MENU_SECTION_ACCORDION_EVENT =
	'OPEN_MENU_SECTION_ACCORDION_EVENT'
export const OPEN_MENU_PRODUCT_ACCORDION_EVENT =
	'OPEN_MENU_PRODUCT_ACCORDION_EVENT'

const emptySection = {
	id: 1,
	name: '',
	nameTranslation: '',
	description: '',
	descriptionTranslation: '',
	products: [emptyProduct],
	visible: true,
}

export const showSectionsSortDialogAtom = atomWithReset<boolean>(false)
export const showProductsSortDialogAtom = atomWithReset<boolean>(false)

export const sectionsListAtom = atomWithReset<TMenuSection[]>([emptySection])
const sectionsAtoms = splitAtom(sectionsListAtom)

export const resetMenuForm = () => {
	getStore().set(showSectionsSortDialogAtom, RESET)
	getStore().set(showProductsSortDialogAtom, RESET)
	getStore().set(sectionsListAtom, RESET)
	getStore().set(activeSectionIdAtom, RESET)
}

export const MenuForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const bus = useBus()

	const sectionIndexToHandleRef = useRef<Record<number, TMenuSectionHandle>>({})

	const [sectionList, setSectionList] = useAtom(sectionsListAtom)
	const [sectionAtoms] = useAtom(sectionsAtoms)

	const [productsReorderState, setProductsReorderState] = useState<{
		products: TMenuSectionProduct[]
		sectionIndex: number
	}>({
		products: [],
		sectionIndex: 0,
	})

	const form = useForm<TMenuFormFields>({
		defaultValues: {
			sections: getStore().get(sectionsListAtom),
		},
		mode: 'onTouched',
		resolver: yupResolver(getMenuFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		clearErrors,
		handleSubmit,
		getValues,
		setFocus,
		setValue,
		trigger,
	} = form

	useSyncFormSectionValidity({ formId: MENU_FORM_ID, isValid })

	const updateFormSections = useCallback(
		(value: TMenuSection[]) => setValue('sections', value),
		[setValue],
	)

	useSyncFieldWithAtom(
		{
			atom: sectionsListAtom,
			setFieldValue: updateFormSections,
		},
		[sectionList.length],
	)

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateMenuForm(
					(onValid, onInvalid) => handleSubmit(onValid, onInvalid),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
					bus,
				),
		}),
		[handleSubmit, bus],
	)

	const handleClickAddSectionButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		bus.emit(CLOSE_MENU_SECTION_ACCORDIONS_EVENT)

		setSectionList((prev) => [
			...prev,
			{ ...emptySection, id: prev.length + 1 },
		])
		setTimeout(() => {
			const newSectionIndex = getValues('sections')!.length - 1
			setFocus(`sections.${newSectionIndex}.name`)
			sectionIndexToHandleRef.current[newSectionIndex]?.scrollIntoView()
		}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
	}

	const handleSectionSortSave = (reorderedSections: TMenuSection[]) => {
		clearErrors('sections')
		trigger('sections')

		setSectionList(reorderedSections)
	}

	const handleProductsReorder = (
		products: TMenuSectionProduct[],
		sectionIndex: number,
	) => {
		setProductsReorderState({ products, sectionIndex })
	}

	const handleProductsSortSave = (reorderedProducts: TMenuSectionProduct[]) => {
		const currentSectionAtom = sectionAtoms[productsReorderState.sectionIndex]
		clearErrors(`sections.${productsReorderState.sectionIndex}`)
		trigger(`sections.${productsReorderState.sectionIndex}`)

		if (!currentSectionAtom) {
			return
		}

		const store = getStore()
		const currentSection = store.get(currentSectionAtom)

		store.set(currentSectionAtom, {
			...currentSection,
			products: reorderedProducts,
		})
	}

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<FormProvider {...form}>
				<div className="flex flex-col gap-6 tablet:gap-8">
					{sectionAtoms.map((sectionAtom, index) => (
						<Section
							key={`${sectionAtom}`}
							ref={(ref) => {
								if (ref) {
									sectionIndexToHandleRef.current[index] = ref
								}
							}}
							index={index}
							sectionAtom={sectionAtom}
							onProductsReorder={handleProductsReorder}
						/>
					))}
				</div>
			</FormProvider>
			<Button
				className="h-10 w-fit self-center px-4 py-2"
				onClick={handleClickAddSectionButton}
			>
				<LinePlus width={16} height={16} />
				{sectionAtoms.length > 0
					? t('generator__content_form_section__menu__add_new_section')
					: t('generator__content_form_section__menu__add_section')}
			</Button>

			<SortDialog
				title={t(
					'generator__content_form_section__sections__sort_dialog_title',
				)}
				list={sectionList}
				isOpenAtom={showSectionsSortDialogAtom}
				onSave={handleSectionSortSave}
				buttonSize="large"
				renderItem={(item, index, onSortChange) => (
					<SortDialogListItem
						itemsLength={sectionList.length}
						key={item.id}
						name={
							item.name ||
							`${t('generator__content_form_section__menu__sections__default_name')} ${item.id}`
						}
						index={index}
						onSortChange={onSortChange}
					/>
				)}
			/>

			<SortDialog
				title={t(
					'generator__content_form_section__menu__sections__products_sort_dialog_title',
				)}
				list={productsReorderState.products}
				isOpenAtom={showProductsSortDialogAtom}
				onSave={handleProductsSortSave}
				renderItem={(item, index, onSortChange) => (
					<SortDialogListItem
						itemsLength={productsReorderState.products.length}
						key={item.id}
						name={
							item.name ||
							`${t('generator__content_form_section__menu__products__default_name')} ${item.id}`
						}
						index={index}
						onSortChange={onSortChange}
					/>
				)}
			/>
		</div>
	)
})

MenuForm.displayName = 'MenuForm'

export const menuFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: MENU_FORM_ID,
	title: t('generator__content_form_section__menu__title'),
	description: t('generator__content_form_section__menu__description'),
	icon: <LineRestaurant className="text-info-color fill-info-color" />,
	Content: MenuForm,
})
